// Note this file was deprecated by ascPlugin.ts

import PluginInterface, { jsonBodyData } from './pluginInterface'
import { UserMessage, BotMessages, BotButtons } from './msgInterface'

type data = {
    plugin: string
    endpoint_url: string
    sender_id: string
    initial_payload: string
    auth_token: string
    timeout: number
    testing: object | null
}

type rasaResponse = {
    action?: string //e.g. pass_control
    payload?: string
    target?: string //e.g. plugin name
    recipient_id?: string
    image?: string
    buttons?: Array<{
        payload: string
        title: string
    }>
    quick_replies?: Array<{
        payload: string
        title: string
    }>
    custom?: {
        text: string // GTP Generated text
        source: string // original text
    }
    text?: string
}

export const emptyMessage = 'Sorry, I am unable to respond to this at the moment.'


function splitRasaResponsesByTextParagraph (responses: rasaResponse[]): rasaResponse[] {
    return responses.map(response => {
        if (response.custom || response.text) {
            let rasaTextField = response.custom ? response.custom.text : response.text
            if (rasaTextField) {
                let newResponses: rasaResponse[] = rasaTextField.split("\n\n").map(paragraph => {
                    return {
                        recipient_id: response.recipient_id,
                        text: paragraph,
                    }
                })
                newResponses[newResponses.length - 1] = {
                    ...response,
                    text: newResponses[newResponses.length - 1].text,
                }
                return newResponses
            }
        }
        return response
    }).flat()
}

export default class RasaPlugin implements PluginInterface {
    REQUIRED_DATA = []
    data: data

    constructor(data: data) {
        this.data = data
    }

    init() {
        // initialise or make changes to data if required and place in constructor.
    }

    validateData() {
        if (!this.data) throw new Error('No data provided.')
    }

    async apiCall(endpoint: string, jsonBodyData: jsonBodyData, timeout: number): Promise<rasaResponse[] | boolean> {
        const controller = new AbortController()
        let fetchInit = {
            method: 'POST',
            signal: controller.signal,
            body: JSON.stringify(jsonBodyData),
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        }

        // timeout for if server is unresponsive:
        const timeoutId = setTimeout(
            () => {
                let message = 'Request timed out'
                controller.abort()
                if (this.data.testing) console.error(message)
                return false
            },
            timeout > 0 ? timeout : 10000 // when running cypress tests timeout = 0, so set default timeout to 10 seconds
        )

        //try send, catch err
        try {
            let response = await fetch(endpoint, fetchInit)
            clearTimeout(timeoutId)
            let responseOK = response?.ok ?? false
            let data = await response.json()
            if (this.data.testing) console.log('Response from Rasa: ', data, '\nStatus: ', response.status)
            if (responseOK) {
                return data
            } else {
                if (this.data.testing) console.log('Bad response.')
            }
            return responseOK
        } catch (e) {
            clearTimeout(timeoutId)
            if (this.data.testing) console.error(e)
            return false
        }
    }

    dataConverter(success: rasaResponse[]): BotMessages {
        let response: BotMessages = []
        if (success.length)
            splitRasaResponsesByTextParagraph(success).forEach(item => {
                let widgetButtons: BotButtons = []
                // buttons and quick replies both offer same functionality
                item?.buttons?.forEach(button => {
                    widgetButtons.push({
                        text: button.title,
                        payload: button.payload,
                    })
                })
                item?.quick_replies?.forEach(reply => {
                    widgetButtons.push({
                        text: reply.title,
                        payload: reply.payload,
                    })
                })

                let responseBody: any = {
                    message: item.text,
                    buttons: widgetButtons,
                }

                if (!responseBody.message) responseBody.message = item.image ? '' : emptyMessage
                
                if (item?.custom) responseBody.custom = {source: item.custom.source}

                if (item?.image) responseBody.image = item.image

                if (item?.action) {
                    const { action, payload, target } = item
                    if (action === 'pass_control') {
                        responseBody.pass_control = target
                        responseBody.payload = payload
                    }
                }
                if (responseBody.message === emptyMessage) {
                    if (this.data.testing) console.error('Error: the API response message/text field came back empty')
                }
                response.push(responseBody)
            })
        else response.push({ message: emptyMessage })

        return response
    }

    async processInput(input: UserMessage, timeout: number): Promise<BotMessages | object | boolean | undefined> {
        if (input.payload === "/restart") {
            let passControlResponse: Array<object> = [{
                pass_control: "cover_page_plugin",
                message: "reset",
                payload: "/resetCoverpage"
            }]
            return passControlResponse
        } else {
            let message = input.payload || input.message
            let callResult = await this.apiCall(this.data.endpoint_url, { message, sender: input.sender_id }, timeout)
            let botMessages: BotMessages = []
            if (typeof callResult == 'boolean') {
                botMessages.push({
                    message: emptyMessage,
                })
            } else {
                let success: rasaResponse[] = callResult
                let response = this.dataConverter(success)
                botMessages = response
            }
            return botMessages
        }
    }

    sideEffects(value: any): any {
        // side effects such as analytics/storage of info in a db/chat history etc.
    }
}
